import axios from 'axios';
import endpoints from '../utils/endpoints';
import { accessToken } from '../utils/token';

export interface BulkEmailResendResponse {
  success: boolean;
  message: string;
  data: {
    jobId: string;
    eventId: string;
    eventName: string;
    totalOrders: number;
    status: string;
    estimatedDuration: string;
  };
}

export interface BulkEmailStatus {
  jobId: string;
  eventId: string;
  eventName: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress: number;
  totalOrders: number;
  emailsSent: number;
  emailsFailed: number;
  startedAt: string;
  completedAt?: string;
  duration?: number;
  estimatedTimeRemaining?: number;
  errorMessage?: string;
  failedEmails: Array<{
    orderId: string;
    email: string;
    error: string;
    timestamp: string;
  }>;
  createdBy: string;
  recentLogs: Array<{
    order_id: string;
    email: string;
    status: string;
    sent_at: string;
    error_message?: string;
  }>;
}

export interface BulkEmailStatusResponse {
  success: boolean;
  data: BulkEmailStatus;
}

export interface BulkEmailHistoryJob {
  id: number;
  event_name: string;
  total_orders: number;
  emails_sent: number;
  emails_failed: number;
  status: string;
  started_at: string;
  completed_at?: string;
  duration_seconds?: number;
  created_by: string;
}

export interface BulkEmailHistoryResponse {
  success: boolean;
  data: {
    jobs: BulkEmailHistoryJob[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  };
}

export interface BulkEmailQueueStats {
  waiting: number;
  active: number;
  completed: number;
  failed: number;
}

export interface BulkEmailQueueStatsResponse {
  success: boolean;
  data: BulkEmailQueueStats;
}

export interface RetryFailedEmailsResponse {
  success: boolean;
  message: string;
  data: {
    retryJobId: string;
    totalRetryEmails: number;
  };
}

const triggerBulkEmailResend = async (eventId: string): Promise<BulkEmailResendResponse> => {
  const url = endpoints.BULK_EMAIL_RESEND(eventId);
  
  const { data: response } = await axios.post(url, {}, {
    headers: {
      Authorization: accessToken,
    },
  });
  
  return response;
};

const getBulkEmailResendStatus = async (eventId: string): Promise<BulkEmailStatusResponse> => {
  const url = endpoints.BULK_EMAIL_RESEND_STATUS(eventId);
  
  const { data: response } = await axios.get(url, {
    headers: {
      Authorization: accessToken,
    },
  });
  
  return response;
};

const getBulkEmailResendHistory = async (
  eventId: string,
  page: number = 1,
  limit: number = 10
): Promise<BulkEmailHistoryResponse> => {
  const url = endpoints.BULK_EMAIL_RESEND_HISTORY(eventId);
  
  const { data: response } = await axios.get(url, {
    headers: {
      Authorization: accessToken,
    },
    params: { page, limit }
  });
  
  return response;
};

const getBulkEmailQueueStats = async (): Promise<BulkEmailQueueStatsResponse> => {
  const url = endpoints.BULK_EMAIL_QUEUE_STATS;
  
  const { data: response } = await axios.get(url, {
    headers: {
      Authorization: accessToken,
    },
  });
  
  return response;
};

const retryFailedEmails = async (eventId: string, jobId: string): Promise<RetryFailedEmailsResponse> => {
  const url = endpoints.BULK_EMAIL_RESEND_RETRY(eventId);
  
  const { data: response } = await axios.post(url, { jobId }, {
    headers: {
      Authorization: accessToken,
    },
  });
  
  return response;
};

const service = {
  triggerBulkEmailResend,
  getBulkEmailResendStatus,
  getBulkEmailResendHistory,
  getBulkEmailQueueStats,
  retryFailedEmails,
};

export default service;
